import type { CSSProperties } from 'react';

import className from '@rio-cloud/rio-uikit/classNames';

import ActionBarItem from '@rio-cloud/rio-uikit/ActionBarItem';
import Switch from '@rio-cloud/rio-uikit/Switch';
import Icon from '@rio-cloud/rio-uikit/Icon';
import SimpleTooltip from '@rio-cloud/rio-uikit/SimpleTooltip';

type ThemeColors = {
    primary: string;
    secondary: string;
    info: string;
    success: string;
    warning: string;
    danger: string;
};

type ThemeColorSets = {
    light: ThemeColors;
    dark?: ThemeColors;
};

type ThemeSettings = {
    borderRadius: string;
    colorSets: ThemeColorSets;
};

type ThemeStyle = 'DEFAULT' | 'RIO' | 'WIREFRAMES' | 'MAN' | 'SCANIA' | 'TRATON' | 'INTERNATIONAL' | 'VW';
export type ThemeMode = 'light' | 'dark';

type ThemesProps = {
    themeStyle: string;
    themeMode: ThemeMode;
    hasDarkmode: boolean;
    onThemeChange: (style: ThemeStyle, mode?: ThemeMode) => void;
};

type ColorSwatchProps = {
    mode: ThemeMode;
    settings: ThemeSettings;
};

type SelectedThemeIndicatorProps = {
    selected: boolean;
};

type ThemeListItemProps = {
    className: string;
    disabled: boolean;
    label: string;
    mode: ThemeMode;
    onClick: () => void;
    selected: boolean;
    settings: ThemeSettings;
    style?: CSSProperties;
};

const themeDefault: ThemeSettings = {
    borderRadius: '6px',
    colorSets: {
        light: {
            primary: '#0b7184',
            secondary: '#7a4987',
            info: '#287b99',
            success: '#3c815f',
            warning: '#a96318',
            danger: '#b94f58',
        },
        dark: {
            primary: '#58c7c7',
            secondary: '#d6a4d9',
            info: '#8ac8e6',
            success: '#89cfa8',
            warning: '#f2bc79',
            danger: '#f09a9f',
        },
    },
};

const themeRIO: ThemeSettings = {
    borderRadius: '6px',
    colorSets: {
        light: {
            primary: '#30b4c0',
            secondary: '#b23672',
            info: '#4b80a6',
            success: '#5cb85c',
            warning: '#ff8e3c',
            danger: '#e22837',
        },
        dark: {
            primary: '#30b4c0',
            secondary: '#b23672',
            info: '#4b80a6',
            success: '#53a653',
            warning: '#e68333',
            danger: '#dc3545',
        },
    },
};

const themeWireframes: ThemeSettings = {
    borderRadius: '6px',
    colorSets: {
        light: {
            primary: '#536b76',
            secondary: '#7a6a8b',
            info: '#5d7f86',
            success: '#667a5a',
            warning: '#8b7a55',
            danger: '#8a615d',
        },
    },
};

const themeMAN: ThemeSettings = {
    borderRadius: '2px',
    colorSets: {
        light: {
            primary: '#303c49',
            secondary: '#d22b40',
            info: '#4b96d2',
            success: '#71b564',
            warning: '#ff8e3c',
            danger: '#d90000',
        },
        dark: {
            primary: '#435f7d',
            secondary: '#d22b40',
            info: '#4b96d2',
            success: '#71b564',
            warning: '#ff8e3c',
            danger: '#d90000',
        },
    },
};

const themeVW: ThemeSettings = {
    borderRadius: '0px',
    colorSets: {
        light: {
            primary: '#004666',
            secondary: '#b23672',
            info: '#4b80a6',
            success: '#64a844',
            warning: '#ff9b00',
            danger: '#da0c1f',
        },
    },
};

const themeSCANIA: ThemeSettings = {
    borderRadius: '6px',
    colorSets: {
        light: {
            primary: '#081c38',
            secondary: '#132d55',
            info: '#4580e9',
            success: '#3d9e81',
            warning: '#ebba3d',
            danger: '#cd3835',
        },
    },
};

const themeTRATON: ThemeSettings = {
    borderRadius: '0px',
    colorSets: {
        light: {
            primary: '#328092',
            secondary: '#d22b40',
            info: '#4b96d2',
            success: '#71b564',
            warning: '#ff8e3c',
            danger: '#d90000',
        },
        dark: {
            primary: '#328092',
            secondary: '#d22b40',
            info: '#4b96d2',
            success: '#71b564',
            warning: '#ff8e3c',
            danger: '#d90000',
        },
    },
};

const themeInternational: ThemeSettings = {
    borderRadius: '4px',
    colorSets: {
        light: {
            primary: '#ff7144',
            secondary: '#272623',
            info: '#c49471',
            success: '#204a32',
            warning: '#f6ae30',
            danger: '#84211f',
        },
        dark: {
            primary: '#ff7144',
            secondary: '#ffb48f',
            info: '#d1a88c',
            success: '#7dbb64',
            warning: '#ffdc4d',
            danger: '#f37f72',
        },
    },
};

const THEME_DATA_ATTRIBUTE = 'data-theme';

export const STYLE_DEFAULT: ThemeStyle = 'DEFAULT';
export const STYLE_RIO: ThemeStyle = 'RIO';
export const STYLE_WIREFRAMES: ThemeStyle = 'WIREFRAMES';
export const STYLE_MAN: ThemeStyle = 'MAN';
export const STYLE_SCANIA: ThemeStyle = 'SCANIA';
export const STYLE_TRATON: ThemeStyle = 'TRATON';
export const STYLE_INTERNATIONAL: ThemeStyle = 'INTERNATIONAL';
export const STYLE_VW: ThemeStyle = 'VW';

export const getThemeFromDocument = (): string | null => document.documentElement.getAttribute(THEME_DATA_ATTRIBUTE);

export const Themes = ({ themeStyle, themeMode, hasDarkmode, onThemeChange }: ThemesProps) => {
    const handleThemeSwitch = (style: ThemeStyle, mode?: ThemeMode) => {
        onThemeChange(style, mode);
    };

    const listItemClassnames = className(
        'display-flex padding-x-15 padding-y-10',
        'border border-bottom-only border-color-lighter'
    );

    const lastListItemClassnames = className('rounded-bottom');

    const isThemeDisabled = (settings: ThemeSettings) => themeMode === 'dark' && !settings.colorSets.dark;

    const getThemeItemClassnames = (itemStyle: ThemeStyle, settings: ThemeSettings, isLastItem = false) =>
        className(
            listItemClassnames,
            !isThemeDisabled(settings) && 'hover-bg-lightest cursor-pointer',
            themeStyle === itemStyle && 'bg-lightest',
            isLastItem && lastListItemClassnames,
            isThemeDisabled(settings) && 'opacity-50 cursor-not-allowed'
        );

    const wireframesListItemStyles = {
        fontFamily: "'Indie Flower', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    };

    return (
        <ActionBarItem id='themes' mobileDialogBodyClassName='padding-0' hidePopoverOnClick={false} popoverWidth={250}>
            <ActionBarItem.Icon>
                <Icon iconName='color-swatch' />
            </ActionBarItem.Icon>
            <ActionBarItem.Popover
                title={
                    <div className='display-flex align-items-center justify-content-between'>
                        <span>Choose a Theme</span>
                        <ThemeModeSwitch
                            disabled={!hasDarkmode}
                            mode={themeMode}
                            onChange={mode => handleThemeSwitch(themeStyle as ThemeStyle, mode)}
                        />
                    </div>
                }
                className='padding-0'
            >
                <div>
                    <ThemeListItem
                        className={getThemeItemClassnames(STYLE_DEFAULT, themeDefault)}
                        disabled={isThemeDisabled(themeDefault)}
                        label='Default'
                        mode={themeMode}
                        onClick={() => handleThemeSwitch(STYLE_DEFAULT)}
                        selected={themeStyle === STYLE_DEFAULT}
                        settings={themeDefault}
                    />
                    <ThemeListItem
                        className={getThemeItemClassnames(STYLE_RIO, themeRIO)}
                        disabled={isThemeDisabled(themeRIO)}
                        label='RIO'
                        mode={themeMode}
                        onClick={() => handleThemeSwitch(STYLE_RIO)}
                        selected={themeStyle === STYLE_RIO}
                        settings={themeRIO}
                    />
                    <ThemeListItem
                        className={getThemeItemClassnames(STYLE_MAN, themeMAN)}
                        disabled={isThemeDisabled(themeMAN)}
                        label='MAN'
                        mode={themeMode}
                        onClick={() => handleThemeSwitch(STYLE_MAN)}
                        selected={themeStyle === STYLE_MAN}
                        settings={themeMAN}
                    />
                    <ThemeListItem
                        className={getThemeItemClassnames(STYLE_TRATON, themeTRATON)}
                        disabled={isThemeDisabled(themeTRATON)}
                        label='TRATON'
                        mode={themeMode}
                        onClick={() => handleThemeSwitch(STYLE_TRATON)}
                        selected={themeStyle === STYLE_TRATON}
                        settings={themeTRATON}
                    />
                    <ThemeListItem
                        className={getThemeItemClassnames(STYLE_VW, themeVW)}
                        disabled={isThemeDisabled(themeVW)}
                        label='Volkswagen'
                        mode={themeMode}
                        onClick={() => handleThemeSwitch(STYLE_VW)}
                        selected={themeStyle === STYLE_VW}
                        settings={themeVW}
                    />
                    <ThemeListItem
                        className={getThemeItemClassnames(STYLE_SCANIA, themeSCANIA)}
                        disabled={isThemeDisabled(themeSCANIA)}
                        label='SCANIA'
                        mode={themeMode}
                        onClick={() => handleThemeSwitch(STYLE_SCANIA)}
                        selected={themeStyle === STYLE_SCANIA}
                        settings={themeSCANIA}
                    />
                    <ThemeListItem
                        className={getThemeItemClassnames(STYLE_INTERNATIONAL, themeInternational)}
                        disabled={isThemeDisabled(themeInternational)}
                        label='International'
                        mode={themeMode}
                        onClick={() => handleThemeSwitch(STYLE_INTERNATIONAL)}
                        selected={themeStyle === STYLE_INTERNATIONAL}
                        settings={themeInternational}
                    />
                    <ThemeListItem
                        className={getThemeItemClassnames(STYLE_WIREFRAMES, themeWireframes, true)}
                        disabled={isThemeDisabled(themeWireframes)}
                        label='Wireframes'
                        mode={themeMode}
                        onClick={() => handleThemeSwitch(STYLE_WIREFRAMES)}
                        selected={themeStyle === STYLE_WIREFRAMES}
                        settings={themeWireframes}
                        style={wireframesListItemStyles}
                    />
                </div>
            </ActionBarItem.Popover>
        </ActionBarItem>
    );
};

const ThemeModeSwitch = ({
    disabled = false,
    mode,
    onChange,
}: {
    disabled?: boolean;
    mode: ThemeMode;
    onChange: (mode: ThemeMode) => void;
}) => (
    <div className='margin-left-10' onClick={event => event.stopPropagation()}>
        <Switch
            checked={mode === 'dark'}
            disabled={disabled}
            enabledText='Dark'
            disabledText='Light'
            autoMinWidth
            onChange={checked => onChange(checked ? 'dark' : 'light')}
        />
    </div>
);

const SelectedThemeIndicator = ({ selected }: SelectedThemeIndicatorProps) =>
    selected ? <Icon iconName='ok' className='margin-right-10' /> : null;

const ThemeListItem = ({
    className,
    disabled,
    label,
    mode,
    onClick,
    selected,
    settings,
    style,
}: ThemeListItemProps) => {
    const themeItem = (
        <div aria-disabled={disabled} className={className} onClick={disabled ? undefined : onClick} style={style}>
            <div className='flex-1-1 text-color-darkest'>{label}</div>
            <SelectedThemeIndicator selected={selected} />
            <ColorSwatch mode={mode} settings={settings} />
        </div>
    );

    return disabled ? (
        <SimpleTooltip content='This theme does not support dark mode.' placement='right'>
            {themeItem}
        </SimpleTooltip>
    ) : (
        themeItem
    );
};

const ColorSwatch = ({ mode, settings }: ColorSwatchProps) => {
    const { borderRadius, colorSets } = settings;
    const colorSet = colorSets[mode] ?? colorSets.light;

    return (
        <div className='display-flex column-gap-5 justify-content-end'>
            <span className='width-15 height-15' style={{ backgroundColor: colorSet.primary, borderRadius }} />
            <span className='width-15 height-15' style={{ backgroundColor: colorSet.secondary, borderRadius }} />
            <span className='width-15 height-15' style={{ backgroundColor: colorSet.info, borderRadius }} />
            <span className='width-15 height-15' style={{ backgroundColor: colorSet.success, borderRadius }} />
            <span className='width-15 height-15' style={{ backgroundColor: colorSet.warning, borderRadius }} />
            <span className='width-15 height-15' style={{ backgroundColor: colorSet.danger, borderRadius }} />
        </div>
    );
};
